import { useState, type ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { AlertTriangle } from 'lucide-react';
import { cn } from '../../lib/cn';
import { Button } from './Button';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red icon + destructive confirm button (deletes, voids). */
  destructive?: boolean;
  /**
   * Runs on confirm. If it returns a promise the confirm button shows a
   * spinner until it settles; the dialog closes when it resolves (surface
   * failures yourself and reject to keep the dialog open).
   */
  onConfirm: () => void | Promise<unknown>;
}

/**
 * Alert-dialog-style confirmation (built on Radix Dialog with alertdialog
 * semantics — outside clicks don't dismiss; Escape cancels).
 *
 * ⚠ **A DESTRUCTIVE DIALOG OPENS WITH THE FOCUS ON CANCEL.** It used to open
 * with the focus on "Void invoice" / "Delete draft": focus was correctly
 * trapped and Escape correctly cancelled, but a keyboard user who hit Enter
 * reflexively — the reflex a dialog trains — irreversibly voided an issued
 * invoice. The safe button is the default; the destructive one costs one Tab.
 * A non-destructive confirm still opens on its confirm button.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  onConfirm,
}: ConfirmDialogProps) {
  const [pending, setPending] = useState(false);

  const handleConfirm = async () => {
    setPending(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch {
      // Caller surfaces the error; keep the dialog open.
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !pending && onOpenChange(next)}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-secondary-950/50 backdrop-blur-sm data-[state=open]:animate-fade-in" />
        <Dialog.Content
          role="alertdialog"
          // Radix sets it on its own `dialog` role; declaring `alertdialog`
          // ourselves means we have to declare this with it, or a screen reader
          // reads the page behind the dialog as still available.
          aria-modal="true"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          className="card fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 animate-scale-in p-6 shadow-elevated focus:outline-none"
        >
          <div className="flex items-start gap-4">
            <div
              className={cn(
                'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
                destructive ? 'bg-status-late-bg text-status-late' : 'bg-grey-100 text-grey-500',
              )}
            >
              <AlertTriangle className="h-5 w-5" aria-hidden />
            </div>
            <div className="min-w-0">
              <Dialog.Title className="text-heading-4 text-secondary">{title}</Dialog.Title>
              <Dialog.Description className="mt-1 text-body-sm text-grey-500">
                {description ?? 'This action cannot be undone.'}
              </Dialog.Description>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={pending}
              autoFocus={destructive}
            >
              {cancelLabel}
            </Button>
            <Button
              variant={destructive ? 'destructive' : 'primary'}
              onClick={handleConfirm}
              loading={pending}
              autoFocus={!destructive}
            >
              {confirmLabel}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
